import t from "@repo/localization/string.macro";
import { SubscriptionPlan as CanadaSubscriptionPlanPb } from "@repo/protobuf/gen/kikoff_canada/protobuf/models/subscription_plan_pb";
import { UserSubscription as CanadaUserSubscriptionPb } from "@repo/protobuf/gen/kikoff_canada/protobuf/models/user_subscription_pb";
import {
  SubscriptionPlan as UsaSubscriptionPlanPb,
  SubscriptionPlanInfo as UsaSubscriptionPlanInfoPb,
} from "@repo/protobuf/gen/web/public/subscription_pb";
import Protobuf, { MessageFrom } from "@repo/protobuf/utils";
import Case from "@repo/utils/Case";
import Enum from "@repo/utils/Enum";
import UObject from "@repo/utils/UObject";

import CreditLine from "./lending/CreditLine.js";

type Subscription = {
  id: Subscription.Id;
  planId: Subscription.Plan.Id;

  startAt: Date;
  endAt: Date;
  terminatedAt?: Date;
};

namespace Subscription {
  export type Id = string & {};
  export const baseName = "Kikoff Credit Service";
  export const tiers = ["basic", "premium", "ultimate"] as const;
  export type Tier = (typeof tiers)[number];
  export namespace Tier {
    export const format = Object.assign(
      (() => {
        // Need to rerun translation on every call
        const map = {
          basic: () => t`Basic`,
          premium: () => t`Premium`,
          ultimate: () => t`Ultimate`,
        } satisfies Record<Tier, () => string>;

        return (tier: Tier) => map[tier]();
      })(),
      {
        plan: (() => {
          // Need to rerun translation on every call
          const map = {
            basic: () => t`Basic plan`,
            premium: () => t`Premium plan`,
            ultimate: () => t`Ultimate plan`,
          } satisfies Record<Tier, () => string>;

          return (tier: Tier) => map[tier]();
        })(),
      }
    );
  }
  export type Resolved = Omit<Subscription, "planId"> & {
    plan: Subscription.Plan;
  };

  export type Plan = {
    id: string;
    name: string;
    durationMonths: number;
    monthlyCostCents: number;
    tier: Tier;
    creditLineAgreement: CreditLine.Agreement;
  };
  export namespace Plan {
    export type Id = string & {};
  }
}

export default Subscription;

export namespace SubscriptionUsa {
  export const normalize = (
    proto: MessageFrom<CanadaUserSubscriptionPb>
  ): Subscription => ({
    id: proto.id,
    planId: proto.plan?.id!,
    startAt: Protobuf.Timestamp.toDate(proto.startAt!),
    endAt: Protobuf.Timestamp.toDate(proto.endAt!),
    terminatedAt: Protobuf.Timestamp.toDate(proto.terminatedAt),
  });
  export namespace Plan {
    export function normalize(proto: MessageFrom<UsaSubscriptionPlanInfoPb>) {
      if (!UObject.hasKey(Tier.byProtoEnum, proto.subscriptionPlan))
        throw new Error(
          `Unsupported subscription plan ${
            UsaSubscriptionPlanPb[proto.subscriptionPlan]
          }, only the following are supported: ${Object.keys(
            Tier.byProtoEnum
          ).map((key) => UsaSubscriptionPlanPb[key as any])}`
        );

      const tier = Tier.byProtoEnum[proto.subscriptionPlan];

      return {
        id: proto.id,
        tier,
        name: Tier.nameByTier[tier],
        durationMonths: proto.durationMonths,
        monthlyCostCents: proto.priceCents / proto.durationMonths,
        // TODO: Add this
        creditLineAgreement: null as any,
      };
    }

    export namespace Tier {
      export const byProtoEnum = {
        [UsaSubscriptionPlanPb.BASIC]: "basic",
        [UsaSubscriptionPlanPb.PREMIUM]: "premium",
        [UsaSubscriptionPlanPb.ULTIMATE]: "ultimate",
      } satisfies Partial<Record<UsaSubscriptionPlanPb, Subscription.Tier>>;
      export const nameByTier = {
        basic: "Kikoff Credit Service",
        premium: "Kikoff Credit Service Premium",
        ultimate: "Kikoff Credit Service Ultimate",
      } satisfies Record<Subscription.Tier, string>;
    }
  }
}

export namespace SubscriptionCanada {
  export const normalize = (
    proto: MessageFrom<CanadaUserSubscriptionPb>
  ): Subscription => ({
    id: proto.id,
    planId: proto.plan?.id!,
    startAt: Protobuf.Timestamp.toDate(proto.startAt!),
    endAt: Protobuf.Timestamp.toDate(proto.endAt!),
    terminatedAt: Protobuf.Timestamp.toDate(proto.terminatedAt),
  });
  export namespace Plan {
    export function normalize(proto: MessageFrom<CanadaSubscriptionPlanPb>) {
      const tier = Tier.normalize(proto.tier);

      return {
        id: proto.id,
        tier,
        durationMonths: proto.durationMonths,
        monthlyCostCents: proto.monthlyCostCents,
        // Proper noun, no need to translate
        name: `Kikoff Credit Service - ${Subscription.Tier.format(tier)}`,
        creditLineAgreement: proto.creditLineAgreement!,
      };
    }

    export namespace Tier {
      export function normalize(
        proto: CanadaSubscriptionPlanPb.Tier
      ): Subscription.Tier {
        const tier = Case.fromConstant(
          Enum.keyOf(CanadaSubscriptionPlanPb.Tier, proto)
        ).toCamel();

        if (tier === "unknown")
          throw new Error("Unexpected UNKNOWN tier in subscription plan.");

        return tier;
      }
    }
  }
}
